import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { BookingModal } from '../../components/booking-modal/booking-modal';
import { MetaService } from '../../services/meta.service';
import { SiteSettingsService } from '../../services/site-settings.service';
import { TeamMemberService, TeamMember } from '../../services/team-member.service';

@Component({
  selector: 'app-about-us',
  imports: [CommonModule, RouterModule, BookingModal],
  templateUrl: './about-us.html',
  styleUrl: './about-us.css',
})
export class AboutUs implements OnInit {
  showBookingModal = signal(false);

  // Team Members
  teamMembers = signal<TeamMember[]>([]);
  currentTeamIndex = signal<number>(0); // 0 = team image, 1+ = team members
  private teamMemberService = inject(TeamMemberService);
  private settings = inject(SiteSettingsService);

  // Site Settings — already loaded via APP_INITIALIZER
  heroTagline = signal(this.settings.val('about_hero', 'about_hero_tagline', 'Our Story'));
  heroTitle = signal(this.settings.val('about_hero', 'about_hero_title', 'Restoring confidence, one strand at a time'));
  heroDescription = signal(this.settings.val('about_hero', 'about_hero_description', 'Founded in Hyderabad, OG Hair Studio has been transforming lives through world-class hair restoration services since 2015.'));

  purposeTagline = signal(this.settings.val('about_purpose', 'about_purpose_tagline', 'Our Purpose'));
  purposeTitle = signal(this.settings.val('about_purpose', 'about_purpose_title', 'We believe everyone deserves to feel confident'));
  purposeParagraph1 = signal(this.settings.val('about_purpose', 'about_purpose_paragraph1', 'Hair loss affects millions of people, impacting not just appearance but self-esteem and quality of life. We started OG Hair Studio with a simple mission: to provide accessible, world-class hair restoration that truly transforms lives.'));
  purposeParagraph2 = signal(this.settings.val('about_purpose', 'about_purpose_paragraph2', 'Our approach combines cutting-edge medical technology with personalized care. Every treatment plan is crafted individually, because no two journeys are the same.'));
  purposeYears = signal(this.settings.val('about_purpose', 'about_purpose_years', '10+'));
  purposeYearsText = signal(this.settings.val('about_purpose', 'about_purpose_years_text', 'Years of Excellence'));
  purposeImage = signal(this.settings.url('about_purpose', 'about_purpose_image') || '/images/studio.png');

  valuesTagline = signal(this.settings.val('about_values', 'about_values_tagline', 'What Guides Us'));
  valuesTitle = signal(this.settings.val('about_values', 'about_values_title', 'Our Values'));
  value1Title = signal(this.settings.val('about_values', 'about_value1_title', 'Excellence'));
  value1Description = signal(this.settings.val('about_values', 'about_value1_description', 'We never compromise on quality. From the products we use to the techniques we employ, excellence is our standard.'));
  value2Title = signal(this.settings.val('about_values', 'about_value2_title', 'Transparency'));
  value2Description = signal(this.settings.val('about_values', 'about_value2_description', 'Honest consultations, clear pricing, realistic expectations. We build trust through complete transparency.'));
  value3Title = signal(this.settings.val('about_values', 'about_value3_title', 'Compassion'));
  value3Description = signal(this.settings.val('about_values', 'about_value3_description', 'We understand the emotional journey of hair loss. Our team provides not just treatment, but genuine support.'));

  founderTagline = signal(this.settings.val('about_founder', 'about_founder_tagline', 'Meet The Founder'));
  founderName = signal(this.settings.val('about_founder', 'about_founder_name', 'Rajesh Sharma'));
  founderRole = signal(this.settings.val('about_founder', 'about_founder_role', 'Founder & CEO'));
  founderBio1 = signal(this.settings.val('about_founder', 'about_founder_bio1', 'With over a decade of experience in hair care and restoration, Rajesh has helped thousands of clients regain their confidence.'));
  founderBio2 = signal(this.settings.val('about_founder', 'about_founder_bio2', 'His journey began as a passionate enthusiast, leading him to pursue specialized training across India and internationally before founding OG Hair Studio in 2015.'));
  founderQuote = signal(this.settings.val('about_founder', 'about_founder_quote', '"Every client\'s journey is unique. I believe in creating personalized solutions that deliver natural-looking, long-lasting results."'));
  founderImage = signal(this.settings.url('about_founder', 'about_founder_image') || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=750&fit=crop');

  teamTagline = signal(this.settings.val('about_team', 'about_team_tagline', 'The People Behind OG'));
  teamTitle = signal(this.settings.val('about_team', 'about_team_title', 'Our Team'));
  teamDescription = signal(this.settings.val('about_team', 'about_team_description', 'A dedicated team of certified professionals with decades of combined experience in hair restoration and aesthetic care.'));
  teamImage = signal(this.settings.url('about_team', 'about_team_image') || 'https://imagehairstudio.com.au/wp-content/uploads/2024/10/IH2oct2024_130-scaled.jpg');

  ctaTitle = signal(this.settings.val('about_cta', 'about_cta_title', 'Ready to start your journey?'));
  ctaDescription = signal(this.settings.val('about_cta', 'about_cta_description', 'Book a free consultation and discover the best solution for your hair restoration needs.'));
  ctaPhone = signal(this.settings.val('contact', 'contact_phone', '+91 98765 43210'));

  private metaService = inject(MetaService);

  ngOnInit() {
    this.metaService.setPageMeta('about');
    this.loadTeamMembers();
  }

  loadTeamMembers() {
    this.teamMemberService.getActive().subscribe({
      next: (res) => {
        if (res.status) this.teamMembers.set(res.data);
      },
    });
  }

  scrollTeam(direction: 'left' | 'right') {
    const totalItems = this.teamMembers().length + 1; // +1 for team image
    const current = this.currentTeamIndex();

    if (direction === 'right') {
      if (current < totalItems - 1) {
        this.currentTeamIndex.set(current + 1);
      }
    } else {
      if (current > 0) {
        this.currentTeamIndex.set(current - 1);
      }
    }
  }

  getCurrentMember(): TeamMember | null {
    const index = this.currentTeamIndex();
    if (index === 0) return null; // Team image
    return this.teamMembers()[index - 1] || null;
  }

  getTeamIndices(): number[] {
    const arr = [0];
    for (let i = 0; i < this.teamMembers().length; i++) {
      arr.push(i + 1);
    }
    return arr;
  }

  openBookingModal() {
    this.showBookingModal.set(true);
  }

  closeBookingModal() {
    this.showBookingModal.set(false);
  }
}
